'use client'

import * as React from 'react'
import { motion } from 'motion/react'

import cn from '@config/tailwind/cn'

import type { MediaType } from './types'

interface PostType {
  id?: string
  title?: string
  slug?: string
  description?: string | null
  featureImage?: MediaType | string
  tags?: { slug: string; name?: string }
  author?: { name?: string } | string
}

interface BlogShowcaseBlockFields {
  blockType: 'blog-showcase'
  posts?: PostType | string
}

interface BlogShowcaseBlockProps {
  fields: BlogShowcaseBlockFields
}

export function BlogShowcaseBlock({ fields }: BlogShowcaseBlockProps) {
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const post = typeof fields.posts === 'string' ? null : fields.posts!

  if (!post) {
    return (
      <div className='ui:my-6 ui:rounded-lg ui:border ui:p-8 ui:text-center ui:border-outline ui:bg-grey-10'>
        <p className='ui:text-grey-100'>Blog post not found or not expanded</p>
      </div>
    )
  }

  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const featureImage = typeof post.featureImage === 'string' ? null : post.featureImage!
  const authorName = typeof post.author === 'string' ? null : post.author?.name

  return (
    <motion.div
      className='ui:my-6'
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, ease: [0.32, 0.72, 0, 1] }}
    >
      <motion.a
        href={`/blog/${post.tags?.slug}/${post.slug}`}
        target='_blank'
        className={cn(
          'ui:group ui:flex ui:flex-col ui:md:flex-row ui:overflow-hidden ui:rounded-2xl ui:border ui:shadow-sm',
          'ui:border-outline ui:bg-neutral-white',
        )}
        whileHover={{ y: -4 }}
        transition={{ duration: 0.3 }}
      >
        {/* Content - Left side */}
        <div className='ui:flex ui:flex-1 ui:flex-col ui:justify-center ui:p-6 ui:md:p-8'>
          <h4
            className={cn(
              'ui:mb-3 ui:text-xl ui:font-bold ui:leading-tight ui:transition-colors',
              'ui:text-neutral-black ui:group-hover:text-blue-100',
            )}
          >
            {post.title}
          </h4>
          {post.description && (
            <p className='ui:mb-4 ui:line-clamp-2 ui:text-sm ui:leading-relaxed ui:md:text-base ui:text-grey-100'>
              {post.description}
            </p>
          )}
          <div className='ui:flex ui:items-center ui:gap-2 ui:text-sm ui:text-grey-60'>
            {post.tags?.name && <span className='ui:font-medium ui:text-purple-100'>{post.tags.name}</span>}
            {post.tags?.name && authorName && <span>·</span>}
            {authorName && <span>{authorName}</span>}
          </div>
        </div>

        {/* Image - Right side */}
        {featureImage?.url && (
          <div className='ui:relative ui:h-48 ui:w-full ui:overflow-hidden ui:md:h-auto ui:md:w-70 ui:shrink-0'>
            <motion.img
              src={featureImage.url}
              alt={featureImage.alt ?? post.title ?? ''}
              className='ui:h-full ui:w-full ui:object-cover'
              loading='lazy'
              initial={false}
              whileHover={{ scale: 1.05 }}
              transition={{ duration: 0.4 }}
            />
          </div>
        )}
      </motion.a>
    </motion.div>
  )
}
